import { AxiosResponse } from 'axios';
import { submissionApi } from './apis';
import { convertToQueryString } from '@utils';
import {
  CreatedSubmissionResponseInterface,
  CreateSubmissionRequestInterface,
  DeleteSubmissionsRequestInterface,
  GenericItemsResponseInterface,
  SubmissionEntityInterface,
  SubmissionQueryInterface,
  UpdateSubmissionRequestInterface,
  UpdateSubmissionsGroupRequestInterface,
  UpdateSubmissionsTypeRequestInterface,
  CreateSampleInterface,
} from '@types';

const ROUTES = {
  getAllSubmissions: '/',
  createSubmission: '/',
  getSubmissionById: '/',
  updateSubmission: '/',
  reassignSubmissionsGroup: '/group',
  reassignSubmissionsType: '/type',
  deleteSubmissions: '/delete',
  createSampleSubmission: '/sample',
};

//caching for getSubmissionById
export const submissionsCache = new Map<string, AxiosResponse<SubmissionEntityInterface>>();

export const getSubmissionById = async (
  id: string,
  withCache = true
): Promise<SubmissionEntityInterface> => {
  const queryKey = `${id}`;

  if (submissionsCache.has(`${id}`) && withCache) {
    return submissionsCache.get(queryKey).data;
  }
  const response = await submissionApi.get(ROUTES.getSubmissionById + id);

  submissionsCache.set(queryKey, response);
  return response.data;
};

export const getAllSubmissions = async (
  query: SubmissionQueryInterface
): Promise<GenericItemsResponseInterface<SubmissionEntityInterface>> => {
  const response = await submissionApi.get(ROUTES.getAllSubmissions + convertToQueryString(query));
  return response.data;
};

export const createSubmission = async (
  data: CreateSubmissionRequestInterface | CreateSampleInterface,
  isSample: boolean
): Promise<CreatedSubmissionResponseInterface> => {
  const response = isSample
    ? await submissionApi.post<CreatedSubmissionResponseInterface>(
        ROUTES.createSampleSubmission,
        data
      )
    : await submissionApi.post<CreatedSubmissionResponseInterface>(ROUTES.createSubmission, data);
  return await getSubmissionById(response.data.id);
};

export const updateSubmission = async (
  id: string,
  data: UpdateSubmissionRequestInterface
): Promise<SubmissionEntityInterface> => {
  await submissionApi.put(ROUTES.updateSubmission + id, data);
  return getSubmissionById(id, false);
};

export const updateSubmissionsType = async (
  data: UpdateSubmissionsTypeRequestInterface
): Promise<void> => {
  const response = await submissionApi.patch(ROUTES.reassignSubmissionsType, data);
  return response.data;
};

export const reassignSubmissionsGroup = async (
  data: UpdateSubmissionsGroupRequestInterface
): Promise<void> => {
  const response = await submissionApi.patch(ROUTES.reassignSubmissionsGroup, data);

  return response.data;
};

export const deleteSubmissions = async (data: DeleteSubmissionsRequestInterface): Promise<void> => {
  const response = await submissionApi.post(ROUTES.deleteSubmissions, data);
  return response.data;
};

export const clearSubmissionCacheValuesByOriginId = (originId: string) => {
  for (const [key, submission] of submissionsCache) {
    if (submission.data.origin.id === originId) {
      submissionsCache.delete(key);
    }
  }
};

export const createSampleSubmission = async (data: CreateSampleInterface) => {
  const response = await submissionApi.post(ROUTES.createSampleSubmission, data);

  return await getSubmissionById(response.data.id);
};
